import { DownOutlined } from "@ant-design/icons";
import { Button, Col, Divider, Form, Input, Popover, Row, Select, Space } from "antd";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

interface Props {
    value: [number | undefined, number | undefined];
    onChange: (value: [number | undefined, number | undefined]) => void;
    className?: string;
}

const CustomButton = styled(Button)`
    width: 100% !important;
    height: 100% !important;
    background-color: #f2f6fd !important;
    border: none !important;
    border-radius: 100px !important;
    font-size: 13px !important;
    font-weight: 400 !important;
    color: black !important;
    padding: 10px 12px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;

    &:hover {
        background-color: #e6edf7 !important;
    }
`;


const formatNumber = (value: string) => {
    const num = value.replace(/\D/g, "");
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const parseNumber = (value: string) => value.replace(/\./g, "");

const priceShorten = (price: number) => {
    if (price >= 1000000000) {
        return `${(price / 1000000000).toFixed(price % 1000000000 === 0 ? 0 : 1)}B`;
    }
    if (price >= 1000000) {
        return `${(price / 1000000).toFixed(price % 1000000 === 0 ? 0 : 1)}M`;
    }
    if (price >= 1000) {
        return `${(price / 1000).toFixed(price % 1000 === 0 ? 0 : 1)}K`;
    }
    return price.toString();
};

interface Values {
    min?: number | string;
    max?: number | string;
}

const ranges = [
    50, 100, 300, 500, 700, 1000, 1300, 1500, 1700, 2000, 2500, 3000, 3500, 5000, 6000, 7000, 8000, 9000, 10000, 25000,
    50000, 75000, 100000,
].map((item) => Number(item + "000000"));

const FilterBudget: React.FC<Props> = ({ value, onChange }) => {
    const [form] = Form.useForm<Values>();
    const [open, setOpen] = useState(false);

    const min = Form.useWatch("min", form);
    const max = Form.useWatch("max", form);

    const label = useMemo(() => {
        if (!value || (value[0] === undefined && value[1] === undefined)) {
            return "All Budget";
        }
        let label = "";
        if (value[0] !== undefined && value[1] !== undefined) {
            label = priceShorten(value[0]) + " - " + priceShorten(value[1]);
        } else if (value[0] !== undefined) {
            label = priceShorten(value[0]) + "+";
        } else if (value[1] !== undefined) {
            label = "Up to " + priceShorten(value[1]);
        }
        return label;
    }, [value]);

    const doSubmit = (values: Values) => {
        let val: [number | undefined, number | undefined] = [
            values.min !== undefined && values.min !== "" ? Number(values.min) : undefined,
            values.max !== undefined && values.max !== "" ? Number(values.max) : undefined,
        ];
        onChange(val);
        setOpen(false);
        setCustomPriceMin("");
        setCustomPriceMax("");
    };

    const minOptions = useMemo(
        () =>
            (!max ? ranges : ranges.filter((price) => price < Number(max))).map((item) => ({
                label: priceShorten(item),
                value: item,
            })),
        [max]
    );

    const maxOptions = useMemo(
        () =>
            (!min ? ranges : ranges.filter((price) => price > Number(min))).map((item) => ({
                label: priceShorten(item),
                value: item,
            })),
        [min]
    );

    const [customPriceMin, setCustomPriceMin] = useState<string>("");
    const [customPriceMax, setCustomPriceMax] = useState<string>("");

    useEffect(() => {
        if (value) {
            form.setFieldsValue({
                min: value[0],
                max: value[1]
            });
        }
    }, [value, form]);

    return (
        <Popover
            placement="bottomLeft"
            title={"Filter Budget"}
            trigger="click"
            open={open}
            onOpenChange={(v) => {
                setOpen(v);
            }}
            content={
                <Form
                    form={form}
                    layout="vertical"
                    style={{ width: 300 }}
                    onFinish={doSubmit}
                >
                    <Row gutter={[12, 0]}>
                        <Col span={12}>
                            <Form.Item
                                label="Minimum"
                                name="min"
                            >
                                <Select
                                    onClear={() => setCustomPriceMin("")}
                                    allowClear
                                    options={
                                        customPriceMin
                                            ? [
                                                {
                                                    label: priceShorten(Number(parseNumber(customPriceMin))),
                                                    value: Number(parseNumber(customPriceMin)),
                                                },
                                            ]
                                            : [
                                                {
                                                    label: "No Min",
                                                    value: "",
                                                },
                                                ...minOptions,
                                            ]
                                    }
                                    dropdownRender={(menu) => (
                                        <>
                                            {menu}
                                            <Divider style={{ margin: "8px 0" }} />
                                            <Space style={{ padding: "0 8px 4px" }}>
                                                <Input
                                                    value={customPriceMin}
                                                    placeholder="Custom Budget"
                                                    onChange={(e) => {
                                                        setCustomPriceMin(formatNumber(e.target.value));
                                                        form.setFieldValue("min", Number(parseNumber(e.target.value)));
                                                    }}
                                                    onKeyPress={(event) => {
                                                        if (!/[0-9]/.test(event.key)) {
                                                            event.preventDefault();
                                                        }
                                                    }}
                                                />
                                            </Space>
                                        </>
                                    )}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Maksimum"
                                name="max"
                            >
                                <Select
                                    onClear={() => setCustomPriceMax("")}
                                    allowClear
                                    options={
                                        customPriceMax
                                            ? [
                                                {
                                                    label: priceShorten(Number(parseNumber(customPriceMax))),
                                                    value: Number(parseNumber(customPriceMax)),
                                                },
                                            ]
                                            : [
                                                {
                                                    label: "No Max",
                                                    value: "",
                                                },
                                                ...maxOptions,
                                            ]
                                    }
                                    dropdownRender={(menu) => (
                                        <>
                                            {menu}
                                            <Divider style={{ margin: "8px 0" }} />
                                            <Space style={{ padding: "0 8px 4px" }}>
                                                <Input
                                                    value={customPriceMax}
                                                    placeholder="Custom Budget"
                                                    onChange={(e) => {
                                                        setCustomPriceMax(formatNumber(e.target.value));
                                                        form.setFieldValue("max", Number(parseNumber(e.target.value)));
                                                    }}
                                                    onKeyPress={(event) => {
                                                        if (!/[0-9]/.test(event.key)) {
                                                            event.preventDefault();
                                                        }
                                                    }}
                                                />
                                            </Space>
                                        </>
                                    )}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Button
                                type="primary"
                                block={true}
                                onClick={form.submit}
                                style={{
                                    borderRadius: '4px',
                                    height: '40px',
                                    fontWeight: 500
                                }}
                            >
                                Apply
                            </Button>
                        </Col>
                    </Row>
                </Form>
            }
        >
            <CustomButton>
                {label}
                <DownOutlined size={14} style={{ color: '#bfbfbf' }} />
            </CustomButton>
        </Popover>
    );
};

export default FilterBudget;
